import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { queryNextAgendaEvent, type NextAgendaEvent } from "@/lib/nextAgendaEvent";
import { eventStartDate, isAllDay } from "@/lib/agendaDates";
import { localeTagFor } from "@/i18n/locale";
import { useLanguage } from "@/i18n/LanguageProvider";

/** The keyed child is discarded on account changes, including pending reads. */
export default function MirrorDayEntry() {
  const [owner, setOwner] = useState<string | null>(null);
  useEffect(() => {
    let active = true, revision = 0;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      revision++;
      if (active) setOwner(session?.user.id || null);
    });
    const startedAt = revision;
    void supabase.auth.getSession().then(({ data, error }) => {
      if (active && revision === startedAt) setOwner(error ? null : data.session?.user.id || null);
    }).catch(() => { if (active && revision === startedAt) setOwner(null); });
    return () => { active = false; subscription.unsubscribe(); };
  }, []);
  return owner ? <MirrorNextEvent key={owner} owner={owner} /> : null;
}

export function MirrorNextEvent({ owner }: { owner: string }) {
  const { t, lang } = useLanguage();
  const [event, setEvent] = useState<NextAgendaEvent | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [attempt, setAttempt] = useState(0);
  const generation = useRef(0);
  useEffect(() => {
    const current = ++generation.current;
    setStatus("loading"); setEvent(null);
    const timer = window.setTimeout(() => {
      if (generation.current === current) { generation.current++; setStatus("error"); }
    }, 12000);
    void queryNextAgendaEvent(owner).then(result => {
      if (generation.current !== current) return;
      if (result.error) throw result.error;
      setEvent(result.data); setStatus("ready");
    }).catch(() => { if (generation.current === current) setStatus("error"); })
      .finally(() => window.clearTimeout(timer));
    return () => { generation.current++; window.clearTimeout(timer); };
  }, [owner, attempt]);
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === "visible") setAttempt(value => value + 1); };
    document.addEventListener("visibilitychange", refresh);
    const timer = window.setInterval(refresh, 60000);
    return () => { document.removeEventListener("visibilitychange", refresh); window.clearInterval(timer); };
  }, []);
  const label = event ? eventStartDate(event).toLocaleDateString(localeTagFor(lang), { day: "numeric", month: "short" }) + " · " +
    (isAllDay(event) ? t("ux.agenda.allDay") : new Date(event.start_at).toLocaleTimeString(localeTagFor(lang), { hour: "2-digit", minute: "2-digit" })) : "";
  return <section aria-label={t("mirror.ready.title")} className="max-w-[680px] mx-auto mb-3 rounded-2xl border border-border bg-background/95 px-4 py-3 shadow-sm">
    {status === "loading" ? <p role="status" className="text-sm text-muted-foreground">{t("mirror.ready.loading")}</p> : status === "error" ? <div className="flex items-center justify-between gap-3">
      <p role="status" className="text-sm text-muted-foreground">{t("mirror.ready.error")}</p>
      <button type="button" onClick={() => setAttempt(value => value + 1)} className="min-h-11 shrink-0 text-sm text-primary focus-visible:ring-2 focus-visible:ring-primary">{t("common.retry")}</button>
    </div> : <Link to={event ? `/agenda?focus=${encodeURIComponent(event.id)}` : "/agenda"} className="min-h-11 flex items-center gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
      <CalendarDays size={19} className="shrink-0 text-primary" aria-hidden="true" />
      <span className="min-w-0 flex-1"><span className="block text-xs text-muted-foreground">{t(event ? "mirror.ready.next" : "mirror.ready.empty")}{event ? ` · ${label}` : ""}</span>
        <span className="mt-1 block text-sm font-medium truncate">{event?.title || t("mirror.ready.open")}</span>
      </span><ArrowUpRight size={18} className="shrink-0 text-primary" aria-hidden="true" />
    </Link>}
  </section>;
}
